import axios from 'axios';
import { store } from '../redux/store';
import { loginSuccess, logout } from '../redux/authSlice';
import oidcAuth from '../services/auth/oidc-client';
import { API_BASE_URL } from '../config/runtimeUrls';
import { buildAuthorizedSession } from '../utils/tokenRoles';
import { isPublicConfigRequest, preparePublicConfigRequest } from './publicConfigRequest';
import { localDevAuthBypass } from '../services/auth/localDevAuthBypass';
import { stripLocalDevCredentials } from '../services/auth/localDevAuthPolicy';

const axiosInstance = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true, // Important: This allows cookies to be sent with requests
    headers: {
        'Content-Type': 'application/json',
    },
});

const allowLocalSandboxWithoutAuth = (): boolean =>
    import.meta.env.DEV &&
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') &&
    window.location.pathname.startsWith('/hours-reporting/test');

// Request interceptor
axiosInstance.interceptors.request.use(
    async (config) => {
        if (preparePublicConfigRequest(config)) return config;
        if (localDevAuthBypass()) {
            return stripLocalDevCredentials(config);
        }
        const state = store.getState();
        const { accessToken, isAuthenticated } = state.auth;
        
        // If we don't have a token in Redux, try to get it from OIDC storage
        if (!accessToken && isAuthenticated) {
            try {
                const user = await oidcAuth.getUser();
                if (user && user.access_token) {
                    store.dispatch(loginSuccess(buildAuthorizedSession(user)));
                    config.headers.Authorization = `Bearer ${user.access_token}`;
                    return config;
                }
            } catch {
                // The existing authenticated-state check below remains authoritative.
            }
        }
        
        // Prevent API calls if not authenticated
        if (!isAuthenticated) {
            if (allowLocalSandboxWithoutAuth()) {
                return config;
            }
            return Promise.reject(new Error('Not authenticated - blocking API call'));
        }
        
        if (accessToken) {
            config.headers.Authorization = `Bearer ${accessToken}`;
        }
        
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor
axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;
        if (isPublicConfigRequest(originalRequest)) return Promise.reject(error);

        // If error is 401 and we haven't tried to refresh the token yet
        if (!localDevAuthBypass() && error.response?.status === 401 && originalRequest && !originalRequest._retry) {
            originalRequest._retry = true;

            try {
                // Use OIDC client to refresh token
                const user = await oidcAuth.refreshToken();
                
                if (user && user.access_token) {
                    // Update the Redux store
                    store.dispatch(loginSuccess(buildAuthorizedSession(user)));

                    // Update the authorization header
                    originalRequest.headers.Authorization = `Bearer ${user.access_token}`;

                    // Retry the original request
                    return axiosInstance(originalRequest);
                } else {
                    // If refresh failed, logout
                    store.dispatch(logout());
                    return Promise.reject(new Error('Token refresh failed'));
                }
            } catch (refreshError) {
                // If refresh token is invalid/expired, logout the user
                store.dispatch(logout());
                return Promise.reject(refreshError);
            }
        }

        return Promise.reject(error);
    }
);

export default axiosInstance;
